import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Spinner, Form, Modal, Button } from "react-bootstrap";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import InputField from "./input/input-field";
import AlertNotificationSimple from "./alert/alert-notification-simple.jsx";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaSearch } from "react-icons/fa";
import axios from "axios";
import Select from "react-select";
import DatePickerField from "./input/date-picker-field";
import Card from "./Card";
import { jwtDecode } from "jwt-decode";
import url from "../configs/config";
import "./JobWorkOrders.css";
import FocusError from "./error/error-focus";
import { roleTypes } from "../data/roleType";
import { useSelector } from "react-redux";
import * as SettingSelector from "../store/setting/selectors";

const JobWorkOrders = ({ jobId, token, collapsedSections, toggleCollapse }) => {
  const navigate = useNavigate();

  const [workOrders, setWorkOrders] = useState([]);
  const [workOrdersLoading, setWorkOrdersLoading] = useState(false);
  const [expandedWoId, setExpandedWoId] = useState(null);
  const [woProducts, setWoProducts] = useState({});
  const [woProductsLoading, setWoProductsLoading] = useState({});
  const [woFilters, setWoFilters] = useState({});
  const [fieldAlert, setFieldAlert] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [editAlert, setEditAlert] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProductWoId, setSelectedProductWoId] = useState(null);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [selectedWoIdForField, setSelectedWoIdForField] = useState(null);
  const [mainAlert, setMainAlert] = useState({
    show: false,
    message: "",
    type: "",
    targetWoId: null,
    isHeaderAlert: false,
  });
  const [inputStatus, setInputStatus] = useState({});
  const [technicians, setTechnicians] = useState([]);
  const [editingWoTechnicians, setEditingWoTechnicians] = useState({});
  const permissions = useSelector(SettingSelector.set_permissions);

  const handleError = (err, targetWoId, type) => {
    const { response } = err;
    if (
      response?.data?.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");
      const decoded = token ? jwtDecode(token) : null;
      navigate(
        `/${
          decoded?.user?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
          : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        },
      );
    } else {
      if (type === "field") {
        setFieldAlert({
          show: true,
          message: response?.data?.message || "Failed to add product",
          type: "danger",
        });
        setTimeout(() => setFieldAlert({ show: false, message: "", type: "" }), 5000);
      } else if (type === "edit") {
        setEditAlert({
          show: true,
          message: response?.data?.message || "Failed to update product",
          type: "danger",
        });
        setTimeout(() => setEditAlert({ show: false, message: "", type: "" }), 5000);
      } else {
        setMainAlert({
          show: true,
          message: response?.data?.message || "An error occurred",
          type: "danger",
          targetWoId: targetWoId,
          isHeaderAlert: false,
        });
        setTimeout(() => setMainAlert({ show: false, message: "", type: "", targetWoId: null, isHeaderAlert: false }), 5000);
      }
    }
  };
  const [editingWoDates, setEditingWoDates] = useState({});
  const [woSearchQueries, setWoSearchQueries] = useState({});
  const alertRef = useRef(null);
  const fieldAlertRef = useRef(null);
  const editAlertRef = useRef(null);
  const [errors, setErrors] = useState({});
  
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [showProductSuggestions, setShowProductSuggestions] = useState(false);
  const [skuSuggestions, setSkuSuggestions] = useState([]);
  const [showSkuSuggestions, setShowSkuSuggestions] = useState(false);
  const suggestionDebounceRef = useRef(null);
  
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [productSearchOffset, setProductSearchOffset] = useState(0);
  const [productSearchHasMore, setProductSearchHasMore] = useState(false);
  
  const [skuSearchTerm, setSkuSearchTerm] = useState("");
  const [skuSearchOffset, setSkuSearchOffset] = useState(0);
  const [skuSearchHasMore, setSkuSearchHasMore] = useState(false);
  
  const [modelSuggestions, setModelSuggestions] = useState([]);
  const [showModelSuggestions, setShowModelSuggestions] = useState(false);
  const [modelSearchTerm, setModelSearchTerm] = useState("");
  const [modelSearchOffset, setModelSearchOffset] = useState(0);
  const [modelSearchHasMore, setModelSearchHasMore] = useState(false);
  
  const [suggestionLoading, setSuggestionLoading] = useState(false);


  const fetchSuggestions = async (searchTerm, type, offset = 0, isLoadMore = false) => {
    if (!searchTerm || searchTerm.length <= 2) {
      if (type === "product_name") {
        setProductSuggestions([]);
        setShowProductSuggestions(false);
      } else if (type === "sku") {
        setSkuSuggestions([]);
        setShowSkuSuggestions(false);
      } else {
        setModelSuggestions([]);
        setShowModelSuggestions(false);
      }
      return;
    }
    
    try {
      setSuggestionLoading(true);
      const config = { headers: { Authorization: `${token}` } };
      const limit = 20;
      const res = await axios.get(
        `${url.backendURL}job/workorders/products/search?search=${encodeURIComponent(searchTerm)}&limit=${limit}&offset=${offset}`,
        config
      );
      const data = res.data?.response?.data || [];
      const totalCount = res.data?.response?.products_count || 0;
      
      if (type === "product_name") {
        if (isLoadMore) {
          setProductSuggestions(prev => [...prev, ...data]);
        } else {
          setProductSuggestions(data);
          setProductSearchTerm(searchTerm);
        }
        setShowProductSuggestions(true);
        setProductSearchOffset(offset + limit);
        setProductSearchHasMore(offset + limit < totalCount);
      } else if (type === "sku") {
        if (isLoadMore) {
          setSkuSuggestions(prev => [...prev, ...data]);
        } else {
          setSkuSuggestions(data);
          setSkuSearchTerm(searchTerm);
        }
        setShowSkuSuggestions(true);
        setSkuSearchOffset(offset + limit);
        setSkuSearchHasMore(offset + limit < totalCount);
      } else {
        if (isLoadMore) {
          setModelSuggestions(prev => [...prev, ...data]);
        } else {
          setModelSuggestions(data);
          setModelSearchTerm(searchTerm);
        }
        setShowModelSuggestions(true);
        setModelSearchOffset(offset + limit);
        setModelSearchHasMore(offset + limit < totalCount);
      }
    } catch (err) {
      console.error(`Error fetching suggestions for ${type}:`, err);
      handleError(err, null);
    } finally {
      setSuggestionLoading(false);
    }
  };

  const handleSuggestionChange = (e, handleChange, type) => {
    handleChange(e);
    const value = e.target.value;
    if (suggestionDebounceRef.current) clearTimeout(suggestionDebounceRef.current);
    
    if (value.length > 2) {
      suggestionDebounceRef.current = setTimeout(() => {
        fetchSuggestions(value, type, 0, false);
      }, 500);
    } else {
      if (type === "product_name") {
        setProductSuggestions([]);
        setShowProductSuggestions(false);
      } else if (type === "sku") {
        setSkuSuggestions([]);
        setShowSkuSuggestions(false);
      } else {
        setModelSuggestions([]);
        setShowModelSuggestions(false);
      }
    }
  };



  const fieldProductSchema = Yup.object().shape({
    product_name: Yup.string()
      .matches(
        /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
        "Product name must contain at least one letter or number",
      )
      .min(1, "Product name must be at least 1 character long")
      .max(255, "Product name must not exceed 255 characters")
      .trim()
      .required("Product Name is required"),
    sku: Yup.string()
      .trim()
      .matches(
        /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
        "SKU must contain at least one letter or number",
      )
      .max(100, "SKU must not exceed 100 characters")
      .required("SKU is required"),
      model: Yup.string()
        .trim()
        .matches(
          /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
          "Model must contain at least one letter or number"
        )
        .min(1, "Model must be at least 1 character long")
        .max(100, "Model must not exceed 100 characters")
        .required("Model is required"),
    quantity: Yup.number()
      .required("Allocated Quantity is required")
      .integer("Allocated Quantity must be an integer")
      .typeError("Allocated Quantity must be a number")
      .positive("Allocated Quantity must be greater than 0"),
    labor_hours: Yup.number()
      .nullable()
      .notRequired()
      .typeError("Labor per hour must be a number")
      .transform((value) => (value === "" ? null : value))
      .test(
        "is-decimal",
        "Labor per hour must have a maximum of 2 decimal places",
        (value) => {
          if (value === null || value === undefined || value === "") {
            return true;
          }
          return /^[+-]?\d+(\.\d{1,2})?$/.test(String(value));
        },
      ),
  });

  const productUpdateSchema = Yup.object().shape({
    product_name: Yup.string().when("source", {
      is: (val) => val === "field_added",
      then: (schema) => schema.required("Product name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    sku: Yup.string().when("source", {
      is: (val) => val === "field_added",
      then: (schema) => schema.required("SKU is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    model: Yup.string().when("source", {
      is: (val) => val === "field_added",
      then: (schema) => schema.required("Model name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    allocated_qty: Yup.number()
      .required("Allocated quantity is required")
      .positive("Must be positive")
      .test(
        "min-installed",
        "Allocated quantity cannot be less than installed quantity",
        function (value) {
          const { source, installed_qty } = this.parent;
          let isField = false;
          if (source === "field_added") {
            isField = true;
          } else if (source) {
            try {
              const src = typeof source === "string" ? JSON.parse(source) : source;
              if (src && src.hasOwnProperty("field_added")) isField = true;
            } catch (e) {}
          }

          if (isField) return true;
          return value >= (installed_qty || 0);
        },
      ),
    labor_hours: Yup.number()
      .min(0, "Labor hours cannot be negative")
      .typeError("Must be a number"),
    installed_qty: Yup.number()
      .min(0, "Installed quantity cannot be negative")
      .test(
        "min-original",
        "Installed quantity cannot be decreased",
        function (value) {
          const { source } = this.parent;
          let isField = false;
          if (source === "field_added") {
            isField = true;
          } else if (source) {
            try {
              const src =
                typeof source === "string" ? JSON.parse(source) : source;
              if (src && src.hasOwnProperty("field_added")) isField = true;
            } catch (e) {}
          }
          if (isField) return true;
          return value >= (editingProduct?.installed_qty || 0);
        },
      )
      .test(
        "max-allocated",
        "Installed quantity cannot exceed allocated quantity",
        function (value) {
          const { source, allocated_qty } = this.parent;
          let isField = false;
          if (source === "field_added") {
            isField = true;
          } else if (source) {
            try {
              const src =
                typeof source === "string" ? JSON.parse(source) : source;
              if (src && src.hasOwnProperty("field_added")) isField = true;
            } catch (e) {}
          }

          if (isField) return true;
          return value <= (allocated_qty || 0);
        },
      )
      .typeError("Must be a number"),
  });

  const fetchTechnicians = async () => {
    try {
      const config = { headers: { Authorization: `${token}` } };
      const res = await axios.get(`${url.backendURL}user/technicians`, config);
      const data = res.data?.response?.data || [];
      setTechnicians(data.map((t) => ({ value: t.id, label: t.name })));
    } catch (err) {
      handleError(err, null, null);
    }
  };

  const updateWorkOrderDetails = async (woId, data) => {
    try {
      const config = { headers: { Authorization: `${token}` } };
      await axios.put(`${url.backendURL}job/workorders/${woId}`, data, config);
      await fetchWorkOrders();
      setMainAlert({
        show: true,
        message: "Work order updated successfully",
        type: "success",
        targetWoId: woId,
        isHeaderAlert: true,
      });
      setTimeout(
        () =>
          setMainAlert({
            show: false,
            message: "",
            type: "",
            targetWoId: null,
            isHeaderAlert: false,
          }),
        5000,
      );
    } catch (err) {
      handleError(err, woId, "main");
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditAlert({ show: false, message: "", type: "" });
    setErrors({});
  };
  const handleCloseFieldModal = () => {
    setShowFieldModal(false);
    setFieldAlert({ show: false, message: "", type: "" });
    setErrors({});
  };
  const fetchWorkOrders = async () => {
    if (!jobId) return;
    setWorkOrdersLoading(true);
    try {
      const config = { headers: { Authorization: `${token}` } };
      const res = await axios.get(
        `${url.backendURL}job/${jobId}/workorders`,
        config,
      );
      setWorkOrders(res.data?.response?.data || []);
    } catch (err) {
      handleError(err, null, null);
      setWorkOrders([]);
    } finally {
      setWorkOrdersLoading(false);
    }
  };

  const fetchWOProducts = async (workorderId, silent = false) => {
    if (!silent)
      setWoProductsLoading((prev) => ({ ...prev, [workorderId]: true }));
    try {
      const config = { headers: { Authorization: `${token}` } };
      const res = await axios.get(
        `${url.backendURL}job/workorders/${workorderId}/products`,
        config,
      );
      setWoProducts((prev) => ({
        ...prev,
        [workorderId]: res.data?.response?.data || [],
      }));
    } catch (err) {
      handleError(err, workorderId, null);
    } finally {
      setWoProductsLoading((prev) => ({ ...prev, [workorderId]: false }));
    }
  };

  const isProductFieldAdded = (product) => {
    if (!product || !product.source) return false;
    if (product.source === "field_added") return true;
    try {
      const src =
        typeof product.source === "string"
          ? JSON.parse(product.source)
          : product.source;
      return src && src.hasOwnProperty("field_added");
    } catch (e) {
      return product.source === "field_added";
    }
  };

  const capitalizeProductName = (str) => {
    if (!str || typeof str !== "string") return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const truncateProductName = (str, n = 50) => {
    if (!str || typeof str !== "string") return "";
    return str.length > n ? str.substr(0, n - 1) + "..." : str;
  };

  const handleEditProduct = (woId, product, woStatus) => {
    setErrors({});
    setEditAlert({ show: false, message: "", type: "" });
    setEditingProduct({ ...product, woStatus });
    setSelectedProductWoId(woId);
    setShowEditModal(true);
  };
  const handleSaveProductUpdate = async (values, { setSubmitting }) => {
    const woId = selectedProductWoId;
    try {
      const config = { headers: { Authorization: `${token}` } };
      const isField = isProductFieldAdded(editingProduct);

      const payload = {
        labor_hours: values.labor_hours,
      };

      if (isField) {
        payload.product_name = values.product_name;
        payload.sku = values.sku;
        payload.model = values.model;
        payload.allocated_qty = values.allocated_qty;
        payload.installed_qty = values.allocated_qty;
      } else {
        payload.installed_qty = values.installed_qty;
      }

      await axios.put(
        `${url.backendURL}job/workorders/product/${editingProduct.id}`,
        payload,
        config,
      );

      handleCloseEditModal();
      await fetchWOProducts(woId, true);
      fetchWorkOrders();

      setMainAlert({
        show: true,
        message: "Product updated successfully",
        type: "success",
        targetWoId: woId,
        isHeaderAlert: false,
      });
      setTimeout(
        () =>
          setMainAlert({
            show: false,
            message: "",
            type: "",
            targetWoId: null,
            isHeaderAlert: false,
          }),
        5000,
      );
    } catch (err) {
      handleError(err, woId, "edit");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddFieldProduct = async (
    values,
    { setSubmitting, resetForm },
  ) => {
    try {
      const config = {
        headers: { Authorization: `${token}` },
      };
      await axios.post(
        `${url.backendURL}job/workorders/${selectedWoIdForField}/field-product`,
        values,
        config,
      );

      handleCloseFieldModal();
      resetForm();

      await fetchWOProducts(selectedWoIdForField);
      fetchWorkOrders();
      setWoFilters((prev) => ({ ...prev, [selectedWoIdForField]: "all" }));

      setMainAlert({
        show: true,
        message: "Field product added successfully",
        type: "success",
        targetWoId: selectedWoIdForField,
        isHeaderAlert: false,
      });
      setTimeout(
        () =>
          setMainAlert({
            show: false,
            message: "",
            type: "",
            targetWoId: null,
            isHeaderAlert: false,
          }),
        5000,
      );
    } catch (err) {
      handleError(err, selectedWoIdForField, "field");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (mainAlert.show && alertRef.current) {
      setTimeout(() => {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [mainAlert.show]);

  useEffect(() => {
    if (fieldAlert.show && fieldAlertRef.current) {
      fieldAlertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [fieldAlert.show]);

  useEffect(() => {
    if (editAlert.show && editAlertRef.current) {
      editAlertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [editAlert.show]);

  useEffect(() => {
    if (jobId && token && !collapsedSections.jobWorkorders) {
      fetchWorkOrders();
      fetchTechnicians();
    }
  }, [jobId, token, collapsedSections.jobWorkorders]);

  return (
    <div className="job-work-orders" ref={alertRef}>
      <Row className="mt-0">
        <Col xl="12" lg="12">
          <Card>
            <Card.Header
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => {
                if (collapsedSections.jobWorkorders) fetchWorkOrders();
                toggleCollapse("jobWorkorders");
              }}
            >
              <div className="header-title breadcrumb pb-3">
                <h4
                  className="text-primary card-title"
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/dashboard/app/job-list");
                  }}
                >
                  <span>Job</span>
                </h4>
                <h4 className="mx-2 card-title">{">"} </h4>
                <h4 className="card-title">Work Orders</h4>
              </div>
              <div
                className="sidebar-toggle"
                style={{ position: "initial", marginRight: "1.5rem" }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (collapsedSections.jobWorkorders) fetchWorkOrders();
                  toggleCollapse("jobWorkorders");
                }}
              >
                <i
                  className="icon"
                  style={{
                    transform: collapsedSections.jobWorkorders
                      ? "rotate(270deg)"
                      : "rotate(90deg)",
                  }}
                >
                  <svg
                    width="30"
                    className="icon-20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4.25 12.2744L19.25 12.2744"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                    <path
                      d="M10.2998 18.2988L4.2498 12.2748L10.2998 6.24976"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                  </svg>
                </i>
              </div>
            </Card.Header>

            {!collapsedSections.jobWorkorders && (
              <Card.Body style={{ padding: "16px" }}>
                {workOrdersLoading ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : workOrders.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="mb-3"
                      style={{ opacity: 0.4 }}
                    >
                      <path
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <rect
                        x="9"
                        y="3"
                        width="6"
                        height="4"
                        rx="1"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M9 12h6M9 16h4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    <p className="mb-1 fw-semibold">No work orders found</p>
                    <small>
                      Work orders are created automatically when a job is saved
                      with systems & phases.
                    </small>
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#94a3b8",
                        fontWeight: "500",
                        marginBottom: "16px",
                        paddingLeft: "4px",
                      }}
                    >
                      {workOrders.length} work order
                      {workOrders.length !== 1 ? "s" : ""} ·{" "}
                      {workOrders.reduce(
                        (acc, curr) =>
                          acc + (parseInt(curr.product_count) || 0),
                        0,
                      )}{" "}
                      products
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "14px",
                      }}
                    >
                      {workOrders.map((wo, idx) => {
                        const isExpanded = expandedWoId === wo.id;

                        const statusColor =
                          wo.status === "completed"
                            ? {
                                bg: "#d1fae5",
                                text: "#065f46",
                                dot: "#10b981",
                                border: "#a7f3d0",
                              }
                            : wo.status === "in-progress"
                              ? {
                                  bg: "#fff7ed",
                                  text: "#c2410c",
                                  dot: "#f97316",
                                  border: "#fed7aa",
                                }
                              : {
                                  bg: "#ede9fe",
                                  text: "#5b21b6",
                                  dot: "#7c3aed",
                                  border: "#ddd6fe",
                                };

                        const statusLabel =
                          wo.status === "in-progress"
                            ? "In Progress"
                            : wo.status === "completed"
                              ? "Completed"
                              : "Pending";

                        const progress =
                          wo.total_allocated > 0
                            ? (wo.total_installed / wo.total_allocated) * 100
                            : 0;

                        return (
                          <div
                            key={wo.id}
                            id={`wo-card-${wo.id}`}
                            style={{
                              border: "1px solid #e8eaf0",
                              borderRadius: "10px",
                              overflow: "hidden",
                              boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                              transition: "box-shadow 0.15s",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow =
                                "0 3px 10px rgba(0,0,0,0.09)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow =
                                "0 1px 4px rgba(0,0,0,0.05)";
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "15px 20px",
                                gap: "16px",
                                cursor: "pointer",
                                backgroundColor: isExpanded
                                  ? "#f5f6ff"
                                  : "#ffffff",
                                transition: "background 0.15s",
                              }}
                              onClick={() => {
                                const nextId = isExpanded ? null : wo.id;
                                setExpandedWoId(nextId);
                                if (nextId && !woProducts[nextId]) {
                                  fetchWOProducts(nextId);
                                }
                              }}
                            >
                              <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                style={{
                                  flexShrink: 0,
                                  color: "#94a3b8",
                                  transform: isExpanded
                                    ? "rotate(90deg)"
                                    : "rotate(0deg)",
                                  transition: "transform 0.2s ease",
                                }}
                              >
                                <path
                                  d="M9 18l6-6-6-6"
                                  stroke="currentColor"
                                  strokeWidth="2.2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <div className="wo-title-group">
                                <span
                                  style={{
                                    fontWeight: "700",
                                    fontSize: "15px",
                                    color: "#1e293b",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {wo.work_order_number}
                                </span>

                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "3px 10px",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    fontWeight: "700",
                                    backgroundColor: "#ede9fe",
                                    color: "#5b21b6",
                                    whiteSpace: "nowrap",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    alignSelf: "flex-start",
                                  }}
                                >
                                  {wo.phase_name}
                                </span>
                              </div>

                              <span
                                className="d-none d-sm-inline-flex"
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  padding: "5px 14px",
                                  borderRadius: "20px",
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  backgroundColor: statusColor.bg,
                                  color: statusColor.text,
                                  border: `1px solid ${statusColor.border}`,
                                  whiteSpace: "nowrap",
                                  flexShrink: 0,
                                  marginLeft: "auto",
                                }}
                              >
                                <span
                                  style={{
                                    width: "7px",
                                    height: "7px",
                                    borderRadius: "50%",
                                    backgroundColor: statusColor.dot,
                                    display: "inline-block",
                                    flexShrink: 0,
                                  }}
                                />
                                {statusLabel}
                              </span>
                            </div>

                            <div
                              style={{
                                height: "4px",
                                width: "100%",
                                backgroundColor: "#f1f5f9",
                                position: "relative",
                              }}
                            >
                              <div
                                style={{
                                  height: "100%",
                                  width: `${progress}%`,
                                  backgroundColor:
                                    wo.status === "completed"
                                      ? "#10b981"
                                      : "#f97316",
                                  transition: "width 0.4s ease",
                                }}
                              />
                            </div>

                            {isExpanded && (
                              <div
                                style={{
                                  padding: "0 16px 20px 16px",
                                  backgroundColor: "#ffffff",
                                  borderTop: "1px solid #f0f2f5",
                                }}
                              >
                                {woProductsLoading[wo.id] ? (
                                  <div className="d-flex justify-content-center align-items-center py-4">
                                    <div
                                      className="spinner-border spinner-border-sm text-primary"
                                      role="status"
                                    >
                                      <span className="visually-hidden">
                                        Loading...
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div className="py-4 px-2">
                                      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                                        <div className="w-100">
                                          <div
                                            style={{
                                              fontSize: "16px",
                                              color: "#64748b",
                                              marginTop: "4px",
                                              fontWeight: "500",
                                            }}
                                          >
                                            {wo.work_order_number} -{" "}
                                            {wo.phase_name} - {wo.system_name}
                                          </div>

                                          {mainAlert.show &&
                                            mainAlert.targetWoId === wo.id &&
                                            mainAlert.isHeaderAlert && (
                                              <div className="pt-2 pb-0">
                                                <AlertNotificationSimple
                                                  show={
                                                    mainAlert.show &&
                                                    mainAlert.targetWoId ===
                                                      wo.id &&
                                                    mainAlert.isHeaderAlert
                                                  }
                                                  message={mainAlert.message}
                                                  type={mainAlert.type}
                                                  onClose={() =>
                                                    setMainAlert({
                                                      ...mainAlert,
                                                      show: false,
                                                    })
                                                  }
                                                />
                                              </div>
                                            )}

                                          <div className="d-flex align-items-center gap-2 mt-3 flex-wrap">
                                            <span
                                              style={{
                                                padding: "4px 12px",
                                                borderRadius: "20px",
                                                fontSize: "14px",
                                                fontWeight: "700",
                                                backgroundColor: statusColor.bg,
                                                color: statusColor.text,
                                                border: `1px solid ${statusColor.border}`,
                                              }}
                                            >
                                              {statusLabel}
                                            </span>

                                            <div
                                              style={{
                                                position: "relative",
                                                display: "inline-block",
                                              }}
                                            >
                                              <span
                                                onClick={() => {
                                                  if (wo.status === "completed")
                                                    return;
                                                  setEditingWoDates((prev) => ({
                                                    ...prev,
                                                    [wo.id]: !prev[wo.id],
                                                  }));
                                                }}
                                                style={{
                                                  padding: "6px 16px",
                                                  borderRadius: "30px",
                                                  fontSize: "14px",
                                                  fontWeight: "700",
                                                  backgroundColor: "#f3f6fc",
                                                  color: "#475569",
                                                  border: "1px solid #dfe6f1",
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  gap: "10px",
                                                  cursor:
                                                    wo.status === "completed"
                                                      ? "default"
                                                      : "pointer",
                                                  transition:
                                                    "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                                  boxShadow:
                                                    "0 1px 2px rgba(0,0,0,0.02)",
                                                }}
                                                onMouseEnter={(e) => {
                                                  e.currentTarget.style.backgroundColor =
                                                    "#e9effa";
                                                  e.currentTarget.style.transform =
                                                    "translateY(-1px)";
                                                }}
                                                onMouseLeave={(e) => {
                                                  e.currentTarget.style.backgroundColor =
                                                    "#f1f5f9";
                                                  e.currentTarget.style.transform =
                                                    "translateY(0)";
                                                }}
                                              >
                                                <svg
                                                  width="16"
                                                  height="16"
                                                  viewBox="0 0 48 48"
                                                  fill="none"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                >
                                                  <rect
                                                    x="4"
                                                    y="8"
                                                    width="40"
                                                    height="36"
                                                    rx="6"
                                                    fill="#D2D6FF"
                                                  />
                                                  <rect
                                                    x="4"
                                                    y="8"
                                                    width="40"
                                                    height="12"
                                                    rx="0"
                                                    fill="#4D61FF"
                                                    style={{
                                                      borderRadius:
                                                        "6px 6px 0 0",
                                                    }}
                                                  />
                                                  <path
                                                    d="M12 4V12M36 4V12"
                                                    stroke="#4D61FF"
                                                    strokeWidth="4"
                                                    strokeLinecap="round"
                                                  />
                                                  <rect
                                                    x="12"
                                                    y="24"
                                                    width="4"
                                                    height="4"
                                                    rx="1"
                                                    fill="#4D61FF"
                                                  />
                                                  <rect
                                                    x="22"
                                                    y="24"
                                                    width="4"
                                                    height="4"
                                                    rx="1"
                                                    fill="#4D61FF"
                                                  />
                                                  <rect
                                                    x="32"
                                                    y="24"
                                                    width="4"
                                                    height="4"
                                                    rx="1"
                                                    fill="#4D61FF"
                                                  />
                                                  <rect
                                                    x="12"
                                                    y="34"
                                                    width="4"
                                                    height="4"
                                                    rx="1"
                                                    fill="#4D61FF"
                                                  />
                                                  <rect
                                                    x="22"
                                                    y="34"
                                                    width="4"
                                                    height="4"
                                                    rx="1"
                                                    fill="#4D61FF"
                                                  />
                                                </svg>
                                                {wo.due_date
                                                  ? `Due ${new Date(
                                                      wo.due_date,
                                                    ).toLocaleDateString(
                                                      "en-US",
                                                      {
                                                        month: "short",
                                                        day: "numeric",
                                                        year: "numeric",
                                                      },
                                                    )}`
                                                  : "Set Due Date"}
                                              </span>

                                              {editingWoDates[wo.id] && (
                                                <div
                                                  style={{
                                                    position: "absolute",
                                                    top: "22px",
                                                    left: 0,
                                                    zIndex: 1050,
                                                  }}
                                                >
                                                  <Formik
                                                    initialValues={{}}
                                                    onSubmit={() => {}}
                                                  >
                                                    <DatePickerField
                                                      value={wo.due_date || ""}
                                                      name={`due_date_${wo.id}`}
                                                      hideInput={true}
                                                      managedOpen={true}
                                                      className="m-0 p-0"
                                                      onAutoSubmit={async (
                                                        date,
                                                      ) => {
                                                        await updateWorkOrderDetails(
                                                          wo.id,
                                                          {
                                                            due_date: date,
                                                          },
                                                        );
                                                        setEditingWoDates(
                                                          (prev) => ({
                                                            ...prev,
                                                            [wo.id]: false,
                                                          }),
                                                        );
                                                      }}
                                                      onBlur={() =>
                                                        setEditingWoDates(
                                                          (prev) => ({
                                                            ...prev,
                                                            [wo.id]: false,
                                                          }),
                                                        )
                                                      }
                                                    />
                                                  </Formik>
                                                </div>
                                              )}
                                            </div>
                                            {editingWoTechnicians[wo.id] ? (
                                              <div
                                                style={{
                                                  minWidth: "220px",
                                                  display: "inline-block",
                                                }}
                                              >
                                                <Select
                                                  options={technicians}
                                                  placeholder="Select Technician"
                                                  value={technicians.find(
                                                    (t) =>
                                                      String(t.value) ===
                                                      String(
                                                        (
                                                          wo.assigned_technician_ids ||
                                                          ""
                                                        ).split(",")[0],
                                                      ),
                                                  )}
                                                  onChange={(selected) => {
                                                    const ids = selected
                                                      ? [selected.value]
                                                      : [];
                                                    updateWorkOrderDetails(
                                                      wo.id,
                                                      {
                                                        technician_ids: ids,
                                                      },
                                                    );
                                                    setEditingWoTechnicians(
                                                      (prev) => ({
                                                        ...prev,
                                                        [wo.id]: false,
                                                      }),
                                                    );
                                                  }}
                                                  autoFocus
                                                  onBlur={() =>
                                                    setEditingWoTechnicians(
                                                      (prev) => ({
                                                        ...prev,
                                                        [wo.id]: false,
                                                      }),
                                                    )
                                                  }
                                                  styles={{
                                                    control: (base) => ({
                                                      ...base,
                                                      minHeight: "30px",
                                                      fontSize: "12px",
                                                      borderRadius: "20px",
                                                      borderColor: "#e2e8f0",
                                                    }),
                                                    menu: (base) => ({
                                                      ...base,
                                                      zIndex: 9999,
                                                    }),
                                                  }}
                                                />
                                              </div>
                                            ) : (
                                              <span
                                                onClick={() => {
                                                  if (wo.status === "completed")
                                                    return;
                                                  setEditingWoTechnicians(
                                                    (prev) => ({
                                                      ...prev,
                                                      [wo.id]: true,
                                                    }),
                                                  );
                                                }}
                                                style={{
                                                  padding: "4px 12px",
                                                  borderRadius: "20px",
                                                  fontSize: "14px",
                                                  fontWeight: "600",
                                                  backgroundColor: "#fff",
                                                  color: "#302677",
                                                  border: "1px solid #e1e5f0",
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  gap: "6px",
                                                  cursor:
                                                    wo.status === "completed"
                                                      ? "default"
                                                      : "pointer",
                                                  transition: "all 0.2s",
                                                }}
                                                onMouseEnter={(e) => {
                                                  e.currentTarget.style.backgroundColor =
                                                    "#f8fafc";
                                                }}
                                                onMouseLeave={(e) => {
                                                  e.currentTarget.style.backgroundColor =
                                                    "#fff";
                                                }}
                                              >
                                                <svg
                                                  width="12"
                                                  height="12"
                                                  viewBox="0 0 24 24"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  strokeWidth="2.5"
                                                >
                                                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                                  <circle
                                                    cx="12"
                                                    cy="7"
                                                    r="4"
                                                  />
                                                </svg>
                                                {wo.assigned_technicians ||
                                                  "Assign Tech"}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>

                                      <div className="row g-3 mb-4 summary-grid">
                                        {[
                                          {
                                            label: "TOTAL PRODUCTS",
                                            value: wo.product_count,
                                            sub: "across all sources",
                                            color: "#3a57e8",
                                          },
                                          {
                                            label: "FULLY INSTALLED",
                                            value: wo.fully_installed_count,
                                            sub: "completed lines",
                                            color: "#10b981",
                                          },
                                          {
                                            label: "PARTIAL",
                                            value: wo.partial_count,
                                            sub: "in progress",
                                            color: "#f97316",
                                          },
                                          {
                                            label: "OVER-INSTALLED",
                                            value: wo.over_installed_count,
                                            sub: "exceeds allocation",
                                            color: "#dc2626",
                                          },
                                          {
                                            label: "ALLOTTED LABOR",
                                            value: parseFloat(
                                              wo.total_labor_hours || 0,
                                            ).toFixed(1),
                                            sub: "hours total",
                                            color: "#0ea5e9",
                                          },
                                        ].map((card, cidx) => (
                                          <div key={cidx} className="col">
                                            <div
                                              style={{
                                                backgroundColor: "#ffffff",
                                                border: "1px solid #eef0f8",
                                                borderTop: `4px solid ${card.color}`,
                                                borderRadius: "10px",
                                                padding: "16px",
                                                boxShadow:
                                                  "0 2px 8px rgba(0,0,0,0.03)",
                                                height: "100%",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  fontSize: "12px",
                                                  fontWeight: "700",
                                                  color: "#94a3b8",
                                                  letterSpacing: "0.08em",
                                                  marginBottom: "8px",
                                                }}
                                              >
                                                {card.label}
                                              </div>
                                              <div
                                                style={{
                                                  fontSize: "34px",
                                                  fontWeight: "800",
                                                  color: card.color,
                                                  marginBottom: "4px",
                                                  lineHeight: "1",
                                                }}
                                              >
                                                {card.value}
                                              </div>
                                              <div
                                                style={{
                                                  fontSize: "13px",
                                                  color: "#94a3b8",
                                                  fontWeight: "500",
                                                }}
                                              >
                                                {card.sub}
                                              </div>

                                              <div
                                                style={{
                                                  height: "3px",
                                                  width: "100%",
                                                  backgroundColor: "#f1f5f9",
                                                  marginTop: "12px",
                                                  borderRadius: "2px",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    height: "100%",
                                                    width: "100%",
                                                    backgroundColor: card.color,
                                                    borderRadius: "2px",
                                                    opacity: 0.15,
                                                  }}
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    <div className="mb-3 mt-1 action-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                                      <div className="d-flex align-items-center gap-2 flex-wrap search-filter-group">
                                        <div className="search-box">
                                          <input
                                            placeholder="Search..."
                                            value={woSearchQueries[wo.id] || ""}
                                            onChange={(e) =>
                                              setWoSearchQueries((prev) => ({
                                                ...prev,
                                                [wo.id]: e.target.value,
                                              }))
                                            }
                                            type="text"
                                            aria-controls="user-list-table"
                                            className="form-control  me-2 w-100 rounded"
                                            style={{ paddingRight: "2rem" }}
                                            onFocus={(e) =>
                                              (e.target.style.borderColor =
                                                "#3a57e8")
                                            }
                                            onBlur={(e) =>
                                              (e.target.style.borderColor =
                                                "#e2e8f0")
                                            }
                                          />
                                        </div>

                                        <div className="tab-container">
                                          {[
                                            { id: "all", label: "All" },
                                            {
                                              id: "not_installed",
                                              label: "Not Installed",
                                            },
                                            { id: "partial", label: "Partial" },
                                            {
                                              id: "installed",
                                              label: "Installed",
                                            },
                                            {
                                              id: "over_installed",
                                              label: "Over-Installed",
                                            },
                                            {
                                              id: "field_added",
                                              label: "Field-Added",
                                            },
                                          ].map((tab) => (
                                            <button
                                              key={tab.id}
                                              onClick={() =>
                                                setWoFilters((prev) => ({
                                                  ...prev,
                                                  [wo.id]: tab.id,
                                                }))
                                              }
                                              style={{
                                                padding: "4px 12px",
                                                borderRadius: "20px",
                                                fontSize: "14px",
                                                fontWeight: "600",
                                                border: "1px solid #e1e5f0",
                                                backgroundColor:
                                                  (woFilters[wo.id] ||
                                                    "all") === tab.id
                                                    ? "#3a57e8"
                                                    : "transparent",
                                                color:
                                                  (woFilters[wo.id] ||
                                                    "all") === tab.id
                                                    ? "#ffffff"
                                                    : "#64748b",
                                                transition: "all 0.2s",
                                                cursor: "pointer",
                                                whiteSpace: "nowrap",
                                                margin: "2px",
                                              }}
                                            >
                                              {tab.label}
                                            </button>
                                          ))}
                                        </div>
                                      </div>

                                      <Button
                                        variant="primary"
                                        className="d-flex align-items-center gap-2 add-product-btn"
                                        disabled={wo.status === "completed"}
                                        style={{
                                          borderRadius: "8px",
                                          padding: "8px 16px",
                                        }}
                                        onClick={() => {
                                          setErrors({});
                                          setSelectedWoIdForField(wo.id);
                                          setShowFieldModal(true);
                                        }}
                                      >
                                        <FaPlus size={12} />{" "}
                                        <span className="d-sm-inline d-none">
                                          Add Field Product
                                        </span>
                                        <span className="d-sm-none">
                                          Add Field Product
                                        </span>
                                      </Button>
                                    </div>

                                    <AlertNotificationSimple
                                      show={
                                        mainAlert.show &&
                                        mainAlert.targetWoId === wo.id &&
                                        !mainAlert.isHeaderAlert
                                      }
                                      message={mainAlert.message}
                                      type={mainAlert.type}
                                      onClose={() =>
                                        setMainAlert({
                                          ...mainAlert,
                                          show: false,
                                        })
                                      }
                                    />
                                    <div className="tableWrapper mt-3">
                                      <table className="dataTable">
                                        <thead>
                                          <tr>
                                            <th>PRODUCT</th>
                                            <th className="text-center">
                                              SOURCE
                                            </th>
                                            <th className="text-center header-highlight">
                                              ALLOCATED
                                            </th>
                                            <th className="text-center">
                                              INSTALLED
                                            </th>
                                            <th className="text-center">
                                              REMAINING
                                            </th>
                                            <th className="text-center">
                                              OVERAGE
                                            </th>
                                            <th className="text-center">
                                              LABOR HRS
                                            </th>
                                            <th className="text-center">
                                              STATUS
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(woProducts[wo.id] || []).filter(
                                            (p) => {
                                              const filter =
                                                woFilters[wo.id] || "all";
                                              const query = (
                                                woSearchQueries[wo.id] || ""
                                              ).toLowerCase();
                                              if (
                                                query &&
                                                !(
                                                  p.product_name ||
                                                  p.custom_product_name ||
                                                  ""
                                                )
                                                  .toLowerCase()
                                                  .includes(query) &&
                                                !(
                                                  p.product_sku ||
                                                  p.custom_product_sku ||
                                                  ""
                                                )
                                                  .toLowerCase()
                                                  .includes(query)
                                              )
                                                return false;
                                              if (filter === "all") return true;
                                              if (filter === "field_added") {
                                                const src = p.source;
                                                if (!src) return false;
                                                if (src === "field_added")
                                                  return true;
                                                try {
                                                  const s =
                                                    typeof src === "string"
                                                      ? JSON.parse(src)
                                                      : src;
                                                  return s.hasOwnProperty(
                                                    "field_added",
                                                  );
                                                } catch (e) {
                                                  return src === "field_added";
                                                }
                                              }
                                              return p.status === filter;
                                            },
                                          ).length === 0 ? (
                                            <tr>
                                              <td
                                                colSpan="8"
                                                className="text-center py-5 text-muted"
                                              >
                                                <div className="d-flex flex-column align-items-center">
                                                  <svg
                                                    width="40"
                                                    height="40"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="mb-2"
                                                    style={{ opacity: 0.3 }}
                                                  >
                                                    <circle
                                                      cx="12"
                                                      cy="12"
                                                      r="10"
                                                    />
                                                    <path d="M16 16s-1.5-2-4-2-4 2-4 2" />
                                                    <path d="M9 9h.01" />
                                                    <path d="M15 9h.01" />
                                                  </svg>
                                                  <div>
                                                    No products found matching
                                                    your search or filters.
                                                  </div>
                                                </div>
                                              </td>
                                            </tr>
                                          ) : (
                                            (woProducts[wo.id] || [])
                                              .filter((p) => {
                                                const filter =
                                                  woFilters[wo.id] || "all";
                                                const query = (
                                                  woSearchQueries[wo.id] || ""
                                                ).toLowerCase();
                                                if (
                                                  query &&
                                                  !(
                                                    p.product_name ||
                                                    p.custom_product_name ||
                                                    ""
                                                  )
                                                    .toLowerCase()
                                                    .includes(query) &&
                                                  !(
                                                    p.product_sku ||
                                                    p.custom_product_sku ||
                                                    ""
                                                  )
                                                    .toLowerCase()
                                                    .includes(query)
                                                )
                                                  return false;
                                                if (filter === "all")
                                                  return true;
                                                if (filter === "field_added") {
                                                  const src = p.source;
                                                  if (!src) return false;
                                                  if (src === "field_added")
                                                    return true;
                                                  try {
                                                    const s =
                                                      typeof src === "string"
                                                        ? JSON.parse(src)
                                                        : src;
                                                    return s.hasOwnProperty(
                                                      "field_added",
                                                    );
                                                  } catch (e) {
                                                    return (
                                                      src === "field_added"
                                                    );
                                                  }
                                                }
                                                return p.status === filter;
                                              })
                                              .map((p) => {
                                                const getSources = (src) => {
                                                  if (!src) return {};
                                                  if (typeof src === "object")
                                                    return src;
                                                  try {
                                                    return JSON.parse(src);
                                                  } catch (e) {
                                                    return { [src]: [] };
                                                  }
                                                };
                                                const sources = getSources(
                                                  p.source,
                                                );
                                                const sourceKeys = Object.keys(
                                                  sources,
                                                ).filter(
                                                  (k) =>
                                                    k === "field_added" ||
                                                    (Array.isArray(
                                                      sources[k],
                                                    ) &&
                                                      sources[k].length > 0) ||
                                                    (k !== "field_added" &&
                                                      sources[k]),
                                                );

                                                const sourceColors = {
                                                  bom: {
                                                    bg: "#eef2ff",
                                                    text: "#4338ca",
                                                  },
                                                  quote: {
                                                    bg: "#f5f3ff",
                                                    text: "#6d28d9",
                                                  },
                                                  product_request: {
                                                    bg: "#fef3c7",
                                                    text: "#b45309",
                                                  },
                                                  field_added: {
                                                    bg: "#fff7ed",
                                                    text: "#c2410c",
                                                  },
                                                  purchase_order: {
                                                    bg: "#ecfdf5",
                                                    text: "#047857",
                                                  },
                                                };

                                                const sourceLabels = {
                                                  bom: "BOM",
                                                  product_request:
                                                    "Product Request",
                                                  purchase_order:
                                                    "Purchase Order",
                                                  quote: "Quote",
                                                  field_added: "Field Added",
                                                  project: "Project",
                                                };

                                                const statusColors = {
                                                  not_installed: {
                                                    text: "#94a3b8",
                                                    dot: "#cbd5e1",
                                                    label: "Not Installed",
                                                  },
                                                  partial: {
                                                    text: "#d97706",
                                                    dot: "#fbbf24",
                                                    label: "Partial",
                                                  },
                                                  installed: {
                                                    text: "#059669",
                                                    dot: "#34d399",
                                                    label: "Installed",
                                                  },
                                                  over_installed: {
                                                    text: "#dc2626",
                                                    dot: "#f87171",
                                                    label: "Over-Installed",
                                                  },
                                                  field_added: {
                                                    text: "#d97706",
                                                    dot: "#fbbf24",
                                                    label: "Field-Added",
                                                  },
                                                };
                                                const st = statusColors[
                                                  p.status
                                                ] || {
                                                  text: "#64748b",
                                                  dot: "#94a3b8",
                                                  label: p.status,
                                                };

                                                return (
                                                  <tr
                                                    key={p.id}
                                                    onClick={() =>
                                                      handleEditProduct(
                                                        wo.id,
                                                        p,
                                                        wo.status,
                                                      )
                                                    }
                                                    style={{
                                                      cursor: "pointer",
                                                    }}
                                                    className="product-row-hover"
                                                  >
                                                    <td>
                                                      <div
                                                        className="fw-bold text-dark"
                                                        style={{
                                                          fontSize: "15px",
                                                        }}
                                                        title={p.product_name || p.custom_product_name}
                                                      >
                                                        {(() => {
                                                          const name = isProductFieldAdded(p)
                                                            ? capitalizeProductName(
                                                                p.product_name ||
                                                                  p.custom_product_name,
                                                              )
                                                            : p.product_name ||
                                                              p.custom_product_name;
                                                          return truncateProductName(
                                                            name,
                                                            50,
                                                          );
                                                        })()}
                                                      </div>
                                                      <div className="text-muted">
                                                        {isProductFieldAdded(p)
                                                          ? (
                                                              p.custom_product_sku ||
                                                              p.product_sku ||
                                                              ""
                                                            ).toUpperCase()
                                                          : p.product_sku ||
                                                            p.custom_product_sku ||
                                                            ""}
                                                      </div>
                                                    </td>
                                                    <td className="text-center">
                                                      <div className="d-flex flex-wrap justify-content-center gap-1">
                                                        {sourceKeys.map(
                                                          (key, idx) => {
                                                            const sc =
                                                              sourceColors[
                                                                key
                                                              ] || {
                                                                bg: "#f3f4f6",
                                                                text: "#374151",
                                                              };
                                                            return (
                                                              <React.Fragment
                                                                key={key}
                                                              >
                                                                <span
                                                                  className="sourceBadge"
                                                                  style={{
                                                                    backgroundColor:
                                                                      sc.bg,
                                                                    color:
                                                                      sc.text,
                                                                    fontSize:
                                                                      "10px",
                                                                    padding:
                                                                      "2px 8px",
                                                                  }}
                                                                >
                                                                  {sourceLabels[
                                                                    key
                                                                  ] ||
                                                                    key.replace(
                                                                      "_",
                                                                      " ",
                                                                    )}
                                                                </span>
                                                                {idx <
                                                                  sourceKeys.length -
                                                                    1 && (
                                                                  <span
                                                                    style={{
                                                                      fontSize:
                                                                        "10px",
                                                                      margin:
                                                                        "0 2px",
                                                                    }}
                                                                  >
                                                                    ,
                                                                  </span>
                                                                )}
                                                              </React.Fragment>
                                                            );
                                                          },
                                                        )}
                                                      </div>
                                                    </td>
                                                    <td
                                                      className="text-center fw-bold text-primary"
                                                      style={{
                                                        fontSize: "14px",
                                                      }}
                                                    >
                                                      {parseFloat(
                                                        p.allocated_qty || 0,
                                                      )}
                                                    </td>
                                                    <td
                                                      className="text-center"
                                                      style={{
                                                        fontSize: "14px",
                                                        color:
                                                          parseFloat(
                                                            p.installed_qty ||
                                                              0,
                                                          ) ===
                                                          parseFloat(
                                                            p.allocated_qty ||
                                                              0,
                                                          )
                                                            ? "#94a3b8"
                                                            : "#3a57e8",
                                                      }}
                                                    >
                                                      {parseFloat(
                                                        p.installed_qty || 0,
                                                      )}
                                                    </td>
                                                    <td
                                                      className="text-center"
                                                      style={{
                                                        color:
                                                          (p.allocated_qty ||
                                                            0) -
                                                            (p.installed_qty ||
                                                              0) >
                                                          0
                                                            ? "#1e293b"
                                                            : "#cbd5e1",
                                                      }}
                                                    >
                                                      {(p.allocated_qty || 0) -
                                                        (p.installed_qty || 0) >
                                                      0
                                                        ? (p.allocated_qty ||
                                                            0) -
                                                          (p.installed_qty || 0)
                                                        : "—"}
                                                    </td>
                                                    <td className="text-center">
                                                      {(p.overage_qty || 0) >
                                                      0 ? (
                                                        <span
                                                          style={{
                                                            color: "#dc2626",
                                                            backgroundColor:
                                                              "#fee2e2",
                                                            padding: "3px 8px",
                                                            borderRadius: "4px",
                                                            fontSize: "12px",
                                                            fontWeight: "700",
                                                          }}
                                                        >
                                                          +
                                                          {parseFloat(
                                                            p.overage_qty || 0,
                                                          )}
                                                        </span>
                                                      ) : (
                                                        "—"
                                                      )}
                                                    </td>
                                                    <td
                                                      className="text-center"
                                                      style={{
                                                        fontSize: "14px",
                                                        color: "#64748b",
                                                      }}
                                                    >
                                                      {parseFloat(
                                                        p.labor_hours || 0,
                                                      )}
                                                    </td>
                                                    <td className="text-center">
                                                      <span
                                                        style={{
                                                          display:
                                                            "inline-flex",
                                                          alignItems: "center",
                                                          gap: "5px",
                                                          color: st.text,
                                                          fontWeight: "600",
                                                          fontSize: "12px",
                                                        }}
                                                      >
                                                        <span
                                                          style={{
                                                            width: "6px",
                                                            height: "6px",
                                                            borderRadius: "50%",
                                                            backgroundColor:
                                                              st.dot,
                                                          }}
                                                        />
                                                        {st.label}
                                                      </span>
                                                    </td>
                                                  </tr>
                                                );
                                              })
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </Card.Body>
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        show={showFieldModal}
        onHide={handleCloseFieldModal}
        backdrop="static"
        keyboard={false}
        centered
        size="lg"
      >
        <Modal.Header className="custom-modal-header d-flex  items-center relative ">
          <Modal.Title>Add Field Product</Modal.Title>
          <button
            type="button"
            className="btn-close-svg"
            style={{ position: "absolute", right: "1rem" }}
            onClick={handleCloseFieldModal}
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </Modal.Header>
        <Modal.Body className="p-4">
          {fieldAlert.show && (
            <div ref={fieldAlertRef}>
              <AlertNotificationSimple
                show={true}
                message={fieldAlert.message}
                type={fieldAlert.type}
                onClose={() => setFieldAlert({ ...fieldAlert, show: false })}
              />
            </div>
          )}

          <Formik
            initialValues={{
              product_name: "",
              sku: "",
              model: "",
              quantity: "",
              labor_hours: 0,
            }}
            validationSchema={fieldProductSchema}
            onSubmit={handleAddFieldProduct}
          >
            {({
              handleSubmit,
              isSubmitting,
              values,
              handleChange,
              handleBlur,
              touched,
              errors: formikErrors,
              setFieldValue,
            }) => (
              <Form noValidate onSubmit={handleSubmit} autoComplete="off">
                <FocusError />

                <Row className="pb-5 pt-1">
                  <Col md={6} style={{ position: "relative" }}>
                    <InputField
                      className="col-md-12 form-group"
                      label="Product Name"
                      type="text"
                      name="product_name"
                      asterick={true}
                      value={values.product_name}
                      onChange={(e) => handleSuggestionChange(e, handleChange, "product_name")}
                      onBlur={(e) => {
                        handleBlur(e);
                        setTimeout(() => setShowProductSuggestions(false), 200);
                      }}
                      placeholder="Enter product name"
                      errorMessage={errors.product_name}
                    />
                    {showProductSuggestions && productSuggestions.length > 0 && (
                      <div className="suggestion-dropdown custom-thin-scrollbar" onMouseDown={(e) => e.preventDefault()}>
                        {productSuggestions.map((product) => (
                          <div
                            key={product.id}
                            className="suggestion-item"
                            onMouseDown={() => {
                              setFieldValue("product_name", product.name);
                              setFieldValue("sku", product.sku);
                              setFieldValue("model", product.model || "");
                              setShowProductSuggestions(false);
                            }}
                          >
                            <div className="suggestion-item-name">{product.name}</div>
                            <div className="suggestion-item-sku">
                              <span className="suggestion-item-sku-label">SKU:</span> {product.sku}
                            </div>
                          </div>
                        ))}
                        {productSearchHasMore && (
                          <div
                            className="suggestion-load-more"
                            onMouseDown={(e) => {
                              e.preventDefault(); 
                              fetchSuggestions(productSearchTerm, "product_name", productSearchOffset, true);
                            }}
                          >
                            {suggestionLoading ? "Loading..." : "Load More"}
                          </div>
                        )}
                      </div>
                    )}
                  </Col>
                   <Col md={6}>
                    <Form.Group className="form-group col-md-12">
                      <Form.Label htmlFor="quantity">
                        Allocated Quantity
                        <span className="text-danger">*</span>
                      </Form.Label>

                      <Form.Control
                        type="number"
                        name="quantity"
                        step="1"
                        value={values.quantity}
                        min={1}
                        placeholder="Enter quantity"
                        onWheel={(e) => e.target.blur()}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d+$/.test(value)) {
                            setFieldValue(
                              "quantity",
                              value === "" ? "" : parseInt(value),
                            );
                          }
                        }}
                        style={{
                          width: "100%",
                        }}
                      />
                      <ErrorMessage
                        name={`quantity`}
                        component="div"
                        className="text-danger"
                      />
                      {(() => {
                        const qError = errors.quantity;
                        if (!qError) return null;
                        return Array.isArray(qError) ? (
                          qError.map((error, index) => (
                            <span
                              key={index}
                              className="w-100 mt-1 text-danger"
                            >
                              {error}
                              <br />
                            </span>
                          ))
                        ) : (
                          <span className="w-100 mt-1 text-danger">
                            {qError}
                          </span>
                        );
                      })()}
                    </Form.Group>
                  </Col>
                   <Col md={6} style={{ position: "relative" }}>
                    <InputField
                      className="col-md-12 form-group"
                      label="Model"
                      type="text"
                      name="model"
                      placeholder="Enter model"
                      value={values.model}
                      asterick={true}
                      onChange={(e) => {
                        handleSuggestionChange(e, handleChange, "model");
                        if (values.model === values.sku || values.sku === "") {
                          setFieldValue("sku", e.target.value);
                        }
                      }}
                      onBlur={(e) => {
                        handleBlur(e);
                        setTimeout(() => setShowModelSuggestions(false), 200);
                      }}
                      errorMessage={errors.model}
                    />
                    {showModelSuggestions && modelSuggestions.length > 0 && (
                      <div className="suggestion-dropdown custom-thin-scrollbar" onMouseDown={(e) => e.preventDefault()}>
                        {modelSuggestions.map((product) => (
                          <div
                            key={product.id}
                            className="suggestion-item"
                            onMouseDown={() => {
                              setFieldValue("product_name", product.name);
                              setFieldValue("sku", product.sku);
                              setFieldValue("model", product.model || "");
                              setShowModelSuggestions(false);
                            }}
                          >
                            <div className="suggestion-item-name">{product.name}</div>
                            <div className="suggestion-item-sku">
                              <span className="suggestion-item-sku-label">SKU:</span> {product.sku}
                            </div>
                          </div>
                        ))}
                        {modelSearchHasMore && (
                          <div
                            className="suggestion-load-more"
                            onMouseDown={(e) => {
                              e.preventDefault(); 
                              fetchSuggestions(modelSearchTerm, "model", modelSearchOffset, true);
                            }}
                          >
                            {suggestionLoading ? "Loading..." : "Load More"}
                          </div>
                        )}
                      </div>
                    )}
                  </Col>
                  <Col md={6} style={{ position: "relative" }}>
                    <InputField
                      className="col-md-12 form-group"
                      label="SKU"
                      type="text"
                      name="sku"
                      placeholder="Enter SKU"
                      value={values.sku}
                      asterick={true}
                      onChange={(e) => handleSuggestionChange(e, handleChange, "sku")}
                      onBlur={(e) => {
                        handleBlur(e);
                        setTimeout(() => setShowSkuSuggestions(false), 200);
                      }}
                      errorMessage={errors.sku}
                    />
                    {showSkuSuggestions && skuSuggestions.length > 0 && (
                      <div className="suggestion-dropdown custom-thin-scrollbar" onMouseDown={(e) => e.preventDefault()}>
                        {skuSuggestions.map((product) => (
                          <div
                            key={product.id}
                            className="suggestion-item"
                            onMouseDown={() => {
                              setFieldValue("product_name", product.name);
                              setFieldValue("sku", product.sku);
                              setFieldValue("model", product.model || "");
                              setShowSkuSuggestions(false);
                            }}
                          >
                            <div className="suggestion-item-name">{product.name}</div>
                            <div className="suggestion-item-sku">
                              <span className="suggestion-item-sku-label">SKU:</span> {product.sku}
                            </div>
                          </div>
                        ))}
                        {skuSearchHasMore && (
                          <div
                            className="suggestion-load-more"
                            onMouseDown={(e) => {
                              e.preventDefault(); 
                              fetchSuggestions(skuSearchTerm, "sku", skuSearchOffset, true);
                            }}
                          >
                            {suggestionLoading ? "Loading..." : "Load More"}
                          </div>
                        )}
                      </div>
                    )}
                  </Col>
                 
                  <Col md={6}>
                    <InputField
                      className="col-md-12 form-group"
                      type="text"
                      label="Labor Hours"
                      placeholder="Enter Labor Hours"
                      name="labor_hours"
                      value={values.labor_hours}
                      step="0.01"
                      pattern="^\d+(\.\d{0,2})?$"
                      min={0}
                      onChange={(e) => {
                        let value = e.target.value;
                        if (
                          /^(\d{0,10})(\.\d{0,2})?$/.test(value) ||
                          value === ""
                        ) {
                          const [intPart, decimalPart] = value.split(".");
                          if (intPart.length <= 10) {
                            setFieldValue("labor_hours", value);
                          }
                        }
                      }}
                      onBlur={(e) => {
                        let value = e.target.value;
                        if (value.endsWith(".")) {
                          setFieldValue("labor_hours", value.slice(0, -1));
                        }
                        if (value.startsWith(".")) {
                          setFieldValue("labor_hours", "0" + value);
                        }
                        const num = parseFloat(e.target.value);
                        if (!isNaN(num)) {
                          setFieldValue("labor_hours", num.toFixed(2));
                        }
                      }}
                      errorMessage={errors.labor_hours}
                    />
                  </Col>
                </Row>

                <Modal.Footer>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner size="sm" className="me-2" /> Saving...
                      </>
                    ) : (
                      "Save Product"
                    )}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleCloseFieldModal}
                  >
                    Cancel
                  </Button>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        backdrop="static"
        keyboard={false}
        centered
        size="lg"
      >
        <Modal.Header className="custom-modal-header d-flex  items-center relative ">
          <Modal.Title>
            {editingProduct?.woStatus === "completed"
              ? "View Product Details"
              : "Edit Product Details"}
          </Modal.Title>
          <button
            type="button"
            className="btn-close-svg "
            style={{ position: "absolute", right: "1rem" }}
            onClick={handleCloseEditModal}
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </Modal.Header>
        <Modal.Body className="p-4">
          {editAlert.show && (
            <div ref={editAlertRef}>
              <AlertNotificationSimple
                show={true}
                message={editAlert.message}
                type={editAlert.type}
                onClose={() => setEditAlert({ ...editAlert, show: false })}
              />
            </div>
          )}

          {editingProduct && (
            <Formik
              initialValues={{
                product_name: isProductFieldAdded(editingProduct)
                  ? capitalizeProductName(
                      editingProduct.custom_product_name ||
                        editingProduct.product_name ||
                        "",
                    )
                  : editingProduct.custom_product_name ||
                    editingProduct.product_name ||
                    "",
                sku: isProductFieldAdded(editingProduct)
                  ? (
                      editingProduct.custom_product_sku ||
                      editingProduct.product_sku ||
                      ""
                    ).toUpperCase()
                  : editingProduct.custom_product_sku ||
                    editingProduct.product_sku ||
                    "",
                allocated_qty: editingProduct.allocated_qty || 0,
                installed_qty: editingProduct.installed_qty || 0,
                labor_hours: editingProduct.labor_hours || 0,
                model: editingProduct.custom_product_model || editingProduct.product_model || "",
                source: editingProduct.source,
              }}
              validationSchema={productUpdateSchema}
              onSubmit={handleSaveProductUpdate}
            >
              {({
                handleSubmit,
                isSubmitting,
                values,
                handleChange,
                handleBlur,
                touched,
                errors: formikErrors,
                setFieldValue,
              }) => (
                <Form noValidate onSubmit={handleSubmit} autoComplete="off">
                  <FocusError />

                  <Row className="pb-3 pt-1">
                    {(() => {
                      const isReadOnly =
                        editingProduct.woStatus === "completed";
                      return (
                        <>
                          <Col md={6} style={{ position: "relative" }}>
                            <InputField
                              className="col-md-12 form-group"
                              label="Product Name"
                              type="text"
                              name="product_name"
                              asterick={true}
                              placeholder="Enter product name"
                              value={values.product_name}
                              onChange={(e) => {
                                if (!isReadOnly && isProductFieldAdded(editingProduct)) {
                                  handleSuggestionChange(e, handleChange, "product_name")
                                } else {
                                  handleChange(e)
                                }
                              }}
                              onBlur={(e) => {
                                handleBlur(e);
                                setTimeout(() => setShowProductSuggestions(false), 200);
                              }}
                              disabled={
                                isReadOnly ||
                                !isProductFieldAdded(editingProduct)
                              }
                              errorMessage={errors.product_name}
                            />
                            {showProductSuggestions && productSuggestions.length > 0 && !isReadOnly && isProductFieldAdded(editingProduct) && (
                                <div className="suggestion-dropdown custom-thin-scrollbar">
                                  {productSuggestions.map((product) => (
                                    <div
                                      key={product.id}
                                      className="suggestion-item"
                                      onMouseDown={() => {
                                        setFieldValue("product_name", product.name);
                                        setFieldValue("sku", product.sku);
                                        setFieldValue("model", product.model || "");
                                        setShowProductSuggestions(false);
                                      }}
                                    >
                                      <div className="suggestion-item-name">{product.name}</div>
                                      <div className="suggestion-item-sku">
                                        <span className="suggestion-item-sku-label">SKU:</span> {product.sku}
                                      </div>
                                    </div>
                                  ))}
                                  {productSearchHasMore && (
                                    <div
                                      className="suggestion-load-more"
                                      onMouseDown={(e) => {
                                        e.preventDefault(); 
                                        fetchSuggestions(productSearchTerm, "product_name", productSearchOffset, true);
                                      }}
                                    >
                                      {suggestionLoading ? "Loading..." : "Load More"}
                                    </div>
                                  )}
                                </div>
                              )}
                            </Col>
                            <Col md={6}>
                            <Form.Group className="form-group col-md-12">
                              <Form.Label htmlFor="allocated_qty">
                                Allocated Quantity
                                <span className="text-danger">*</span>
                              </Form.Label>

                              <Form.Control
                                type="number"
                                name="allocated_qty"
                                step="1"
                                placeholder="Enter quantity"
                                value={values.allocated_qty}
                                min={1}
                                disabled={
                                  isReadOnly ||
                                  !isProductFieldAdded(editingProduct)
                                }
                                onWheel={(e) => e.target.blur()}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === "" || /^\d+$/.test(value)) {
                                    setFieldValue(
                                      "allocated_qty",
                                      value === "" ? "" : parseInt(value),
                                    );
                                  }
                                }}
                                style={{
                                  width: "100%",
                                  backgroundColor: (isReadOnly || !isProductFieldAdded(editingProduct))
                                    && "#f0f0f0"
                                    
                                }}
                              />
                              <ErrorMessage
                                name={`allocated_qty`}
                                component="div"
                                className="text-danger"
                              />
                              {(() => {
                                const aError = errors.allocated_qty;
                                if (!aError) return null;
                                return Array.isArray(aError) ? (
                                  aError.map((error, index) => (
                                    <span
                                      key={index}
                                      className="w-100 mt-1 text-danger"
                                    >
                                      {error}
                                      <br />
                                    </span>
                                  ))
                                ) : (
                                  <span className="w-100 mt-1 text-danger">
                                    {aError}
                                  </span>
                                );
                              })()}
                            </Form.Group>
                          </Col>
                          <Col md={6} style={{ position: "relative" }}>
                            <InputField
                              className="col-md-12 form-group"
                              label="Model"
                              type="text"
                              name="model"
                              placeholder="Enter model"
                              value={values.model}
                              onChange={(e) => {
                                if (!isReadOnly && isProductFieldAdded(editingProduct)) {
                                  handleSuggestionChange(e, handleChange, "model");
                                } else {
                                  handleChange(e);
                                }
                                if (values.model === values.sku || values.sku === "") {
                                  setFieldValue("sku", e.target.value);
                                }
                              }}
                              asterick={true}
                              onBlur={(e) => {
                                handleBlur(e);
                                setTimeout(() => setShowModelSuggestions(false), 200);
                              }}
                              disabled={
                                isReadOnly ||
                                !isProductFieldAdded(editingProduct)
                              }
                              errorMessage={errors.model}
                            />
                            {showModelSuggestions && modelSuggestions.length > 0 && !isReadOnly && isProductFieldAdded(editingProduct) && (
                                <div className="suggestion-dropdown custom-thin-scrollbar">
                                  {modelSuggestions.map((product) => (
                                    <div
                                      key={product.id}
                                      className="suggestion-item"
                                      onMouseDown={() => {
                                        setFieldValue("product_name", product.name);
                                        setFieldValue("sku", product.sku);
                                        setFieldValue("model", product.model || "");
                                        setShowModelSuggestions(false);
                                      }}
                                    >
                                      <div className="suggestion-item-name">{product.name}</div>
                                      <div className="suggestion-item-sku">
                                        <span className="suggestion-item-sku-label">SKU:</span> {product.sku}
                                      </div>
                                    </div>
                                  ))}
                                  {modelSearchHasMore && (
                                    <div
                                      className="suggestion-load-more"
                                      onMouseDown={(e) => {
                                        e.preventDefault(); 
                                        fetchSuggestions(modelSearchTerm, "model", modelSearchOffset, true);
                                      }}
                                    >
                                      {suggestionLoading ? "Loading..." : "Load More"}
                                    </div>
                                  )}
                                </div>
                              )}
                          </Col>
                          <Col md={6} style={{ position: "relative" }}>
                            <InputField
                              className="col-md-12 form-group"
                              label="SKU"
                              type="text"
                              name="sku"
                              placeholder="Enter SKU"
                              value={values.sku}
                              asterick={true}
                              onChange={(e) => {
                                if (!isReadOnly && isProductFieldAdded(editingProduct)) {
                                  handleSuggestionChange(e, handleChange, "sku")
                                } else {
                                  handleChange(e)
                                }
                              }}
                              onBlur={(e) => {
                                handleBlur(e);
                                setTimeout(() => setShowSkuSuggestions(false), 200);
                              }}
                              disabled={
                                isReadOnly ||
                                !isProductFieldAdded(editingProduct)
                              }
                              errorMessage={errors.sku}
                            />
                            {showSkuSuggestions && skuSuggestions.length > 0 && !isReadOnly && isProductFieldAdded(editingProduct) && (
                                <div className="suggestion-dropdown custom-thin-scrollbar">
                                  {skuSuggestions.map((product) => (
                                    <div
                                      key={product.id}
                                      className="suggestion-item"
                                      onMouseDown={() => {
                                        setFieldValue("product_name", product.name);
                                        setFieldValue("sku", product.sku);
                                        setFieldValue("model", product.model || "");
                                        setShowSkuSuggestions(false);
                                      }}
                                    >
                                      <div className="suggestion-item-name">{product.name}</div>
                                      <div className="suggestion-item-sku">
                                        <span className="suggestion-item-sku-label">SKU:</span> {product.sku}
                                      </div>
                                    </div>
                                  ))}
                                  {skuSearchHasMore && (
                                    <div
                                      className="suggestion-load-more"
                                      onMouseDown={(e) => {
                                        e.preventDefault(); 
                                        fetchSuggestions(skuSearchTerm, "sku", skuSearchOffset, true);
                                      }}
                                    >
                                      {suggestionLoading ? "Loading..." : "Load More"}
                                    </div>
                                  )}
                                </div>
                              )}
                            </Col>

                          
                          <Col md={6}>
                            <InputField
                              className="col-md-12 form-group"
                              label="Installed Quantity"
                              type="number"
                              name="installed_qty"
                              placeholder="Enter quantity"
                              value={values.installed_qty || 0}
                              max={values.allocated_qty}
                              onChange={handleChange}
                              asterick={true}
                              min={0}
                              onBlur={handleBlur}
                              onWheel={(e) => e.target.blur()}
                              disabled={
                                isReadOnly ||
                                isProductFieldAdded(editingProduct)
                              }
                              error={errors.installed_qty}
                            />
                          </Col>
                          <Col md={6}>
                            <InputField
                              className="col-md-12 form-group"
                              type="text"
                              label="Labor Hours"
                              placeholder="Enter Labor Hours"
                              name="labor_hours"
                              value={values.labor_hours}
                              step="0.01"
                              pattern="^\d+(\.\d{0,2})?$"
                              min={0}
                              disabled={isReadOnly}
                              onChange={(e) => {
                                let value = e.target.value;
                                if (
                                  /^(\d{0,10})(\.\d{0,2})?$/.test(value) ||
                                  value === ""
                                ) {
                                  const [intPart, decimalPart] =
                                    value.split(".");
                                  if (intPart.length <= 10) {
                                    setFieldValue("labor_hours", value);
                                  }
                                }
                              }}
                              onBlur={(e) => {
                                let value = e.target.value;
                                if (value.endsWith(".")) {
                                  setFieldValue(
                                    "labor_hours",
                                    value.slice(0, -1),
                                  );
                                }
                                if (value.startsWith(".")) {
                                  setFieldValue("labor_hours", "0" + value);
                                }
                                const num = parseFloat(e.target.value);
                                if (!isNaN(num)) {
                                  setFieldValue("labor_hours", num.toFixed(2));
                                }
                                handleBlur(e);
                              }}
                              onWheel={(e) => e.target.blur()}
                              errorMessage={errors.labor_hours}
                            />
                          </Col>
                        </>
                      );
                    })()}
                  </Row>

                  <Modal.Footer className="d-flex justify-content-end">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={
                        isSubmitting || editingProduct.woStatus === "completed"
                      }
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner size="sm" className="me-2" /> Updating...
                        </>
                      ) : (
                        "Update"
                      )}
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => setShowEditModal(false)}
                    >
                      Cancel
                    </Button>
                  </Modal.Footer>
                </Form>
              )}
            </Formik>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default JobWorkOrders;
